import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx26ReferenceParams {
  /**
   * 输入参数
   */
  input: {
    /**
     * 视频生成提示词，最大长度2000字符
     * @length 0 - 2000
     */
    prompt: string;
    /**
     * 参考视频URL数组，用于角色/风格一致性，支持1-2个视频。注意：使用时audio必须为false，duration只能为5或10秒
     */
    reference_video_urls?: string[];
    /**
     * 自定义背景音乐URL（可选）
     */
    audio_url?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 视频时长（秒），可选值：5、10（参考视频时不支持15秒）
     * @default 5
     * @values 5 | 10
     */
    duration?: 5 | 10;
    /**
     * 是否启用提示词扩展
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 是否生成音频（使用参考视频时必须为false）
     * @default true
     */
    audio?: boolean;
    /**
     * 是否启用多镜头智能生成模式（仅在prompt_extend=true时生效）
     */
    multi_shots?: boolean;
    /**
     * 随机种子，用于复现生成结果
     */
    seed?: number;
    /**
     * 视频分辨率
     * @default "1920*1080"
     * @values 1280*720 | 720*1280 | 960*960 | 1088*832 | 832*1088 | 1920*1080 | 1080*1920 | 1440*1440 | 1632*1248 | 1248*1632
     */
    size?: '1280*720' | '720*1280' | '960*960' | '1088*832' | '832*1088' | '1920*1080' | '1080*1920' | '1440*1440' | '1632*1248' | '1248*1632';
  };
}

export interface AlibabaWanx26ReferenceResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx26_reference
 *
 * <p>API 文档: https://help.aliyun.com/zh/model-studio/text-to-video-api-reference</p>
 * <p>Alibaba Wanx2.6 参考生视频生成接口，支持最高1080p分辨率，支持参考视频生成（角色/风格一致性），支持多镜头智能生成和音频合成。</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx26Reference } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx26Reference({
 *   // 参数见 AlibabaWanx26ReferenceParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx26ReferenceParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx26Reference(
  params: AlibabaWanx26ReferenceParams,
): Promise<AlibabaWanx26ReferenceResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx26_reference',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx26ReferenceResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
